import { Label } from '@/Components/Label/Label';
import { Procedure } from '@/Components/Procedure/Procedure';
import { type OrderForWorklist, DictationLockType } from '@/Models/GenericWorklist/WorklistResult';
import { getStatusAsString } from '@/Models/Shared/Status';
import { type ColumnDef } from '@tanstack/react-table';
import HallCell from './Columns/Components/HallCell';
import createWorklistColumnHelper from './Columns/WorklistColumnHelper';
import { Urgency } from '@/Components/Urgency/Urgency';
import WorklistDataGridStore from '@/Pages/GenericWorklist/Components/DataGrid/WorklistDataGridStore';
import { DobIcon } from '@dedalusdiit/dob-components';
import React from 'react';
import SelectionCell from '@/Pages/GenericWorklist/Components/DataGrid/SelectionCell';
import InvoiceStatusComponent, { InvoiceStatus } from '@/Components/Invoicing/InvoiceStatus';
import { IWorklistSelectionHelper } from '@/Pages/GenericWorklist/Stores/WorklistSelectionHelper';
import Styles from './WorklistDataGridConfig.module.scss';
import { getTitleForWorklistProperty } from '../../Logic/WorklistProperties';
import { AlignedReportValue, buildAlignedReportValuesWithKeys, buildAlignedReports } from '../../Logic/WorklistReportAlignmentHelper';
import { renderAlignedReportText, ReportTextRowValue } from '../../Logic/WorklistReportAlignment';
import { JustificationCell } from './Columns/Components/JustificationCell';
import { ReportStatusRowValue } from './Columns/Components/ReportStatusCell/ReportStatusCell';
import { ReportStatusColumnCell } from './Columns/Components/ReportStatusCell/ReportStatusColumnCell';
import { REPORT_STATUS } from './Columns/Components/ReportStatusCell/ReportStatusCellHelper';
import SelectionHeaderCell from '@/Pages/GenericWorklist/Components/DataGrid/Header/SelectionHeaderCell';

const worklistColumnHelper = createWorklistColumnHelper();

const formatReportDateTime = (value: Date | string | null | undefined): string =>
    value ? new Date(value).toLocaleString('en-GB') : '';

const handleMouseEnter = (dataGridStore: WorklistDataGridStore, order: OrderForWorklist) => {
    void dataGridStore?.setOrder(order);
};

const handleMouseLeave = (dataGridStore: WorklistDataGridStore) => {
    dataGridStore?.setModalNotVisible();
};

export const getWorklistDataGridColumnSetup = (selectionStore: IWorklistSelectionHelper, dataGridStore?: WorklistDataGridStore): ColumnDef<OrderForWorklist, any>[] => {
    return [
        worklistColumnHelper.createDataColumnDefinition({
            columnId: 'IsSelected',
            headerContent: <SelectionHeaderCell selectionStore={selectionStore} />,
            selectData: (order: OrderForWorklist) => order.OrderInfo.OrderID,
            renderData: (orderId: number) => <SelectionCell id={orderId} selectionStore={selectionStore} />,
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'OrderNumber',
            headerText: getTitleForWorklistProperty('OrderNumber'),
            selectText: (order) => order.OrderInfo.OrderNumber,
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'Patient',
            headerText: getTitleForWorklistProperty('Patient'),
            selectText: ({ PatientInfo: { FirstName, LastName } }) => {
                if (FirstName === null) {
                    return LastName;
                }

                return `${FirstName} ${LastName}`;
            },
        }),
        worklistColumnHelper.createWorklistOrderProcedureTextColumn({
            columnId: 'AccessionNumber',
            headerText: getTitleForWorklistProperty('AccessionNumber'),
            selectText: (orderProcedure) => orderProcedure.AccessionNumber,
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'DoB',
            headerText: getTitleForWorklistProperty('DoB'),
            selectText: (order) => order.PatientInfo.PatientDOB,
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'Age',
            headerText: getTitleForWorklistProperty('Age'),
            selectText: (order) => order.PatientInfo.PatientAge.toString(),
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'ExternalID',
            headerText: getTitleForWorklistProperty('ExternalID'),
            selectText: (order) => order.PatientInfo.PatientExternalID ?? '',
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'NationalNumber',
            headerText: getTitleForWorklistProperty('NationalNumber'),
            selectText: (order) => order.PatientInfo.PatientNationalNumber ?? '',
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'Language',
            headerText: getTitleForWorklistProperty('Language'),
            selectText: (order) => order.PatientInfo.PatientLanguage,
        }),
        worklistColumnHelper.createWorklistOrderLabelColumn({
            columnId: 'Labels',
            headerText: getTitleForWorklistProperty('Labels'),
            selectData: (orderLabel) => orderLabel,
            renderData: (orderLabel) => (
                <Label Name={orderLabel.Name} Abbreviation={orderLabel.Abbreviation} BackgroundColor={orderLabel.BackgroundColor} TextColor={orderLabel.TextColor} />
            ),
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'PatientStatus',
            headerText: getTitleForWorklistProperty('PatientStatus'),
            selectText: (order) => order.PatientInfo.PatientStatus,
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'PatientOrigin',
            headerText: getTitleForWorklistProperty('PatientOrigin'),
            selectText: (order) => order.PatientInfo.PatientOrigin ?? '',
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'VisitNumber',
            headerText: getTitleForWorklistProperty('VisitNumber'),
            selectText: (order) => order.PatientInfo.VisitNumber ?? '',
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'UnitOfCare',
            headerText: getTitleForWorklistProperty('UnitOfCare'),
            selectText: (order) => order.PatientInfo.UnitOfCare ?? '',
        }),
        worklistColumnHelper.createWorklistOrderProcedureColumn({
            columnId: 'Procedures',
            headerText: getTitleForWorklistProperty('Procedures'),
            selectData: (orderProcedure) => orderProcedure,
            renderData: (orderProcedure) => (
                <Procedure
                    item={{
                        Code: orderProcedure.ProcedureInfo.Code,
                        Color: orderProcedure.ProcedureInfo.Color,
                        Description: orderProcedure.ProcedureInfo.Description,
                        Modality_Text: orderProcedure.ProcedureInfo.ModalityName ?? `-`,
                        Room_Text: orderProcedure.HallInfo?.Name ?? null,
                        Radiologist_Text: null,
                        StatusIndicator: 0,
                    }}
                />
            ),
        }),
        worklistColumnHelper.createWorklistOrderProcedureTextColumn({
            columnId: 'ProcedureStatus',
            headerText: getTitleForWorklistProperty('ProcedureStatus'),
            selectText: (procedure) => `${getStatusAsString(procedure.Status)}`,
        }),
        worklistColumnHelper.createWorklistOrderProcedureColumn({
            columnId: 'Halls',
            headerText: getTitleForWorklistProperty('Halls'),
            selectData: (orderProcedure) => orderProcedure.HallInfo,
            renderData: (hallInfo) => <HallCell hallInfo={hallInfo} />,
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'Sites',
            headerText: getTitleForWorklistProperty('Sites'),
            selectText: (order) => order.OrderInfo.SiteName ?? '',
        }),
        worklistColumnHelper.createWorklistOrderProcedureTextColumn({
            columnId: 'ProcedureImplementationDate',
            headerText: getTitleForWorklistProperty('ProcedureImplementationDate'),
            selectText: (procedure) => `${new Date(procedure.ImplementationDate).toLocaleString('en-GB')}`,
        }),
        worklistColumnHelper.createStringArrayColumn({
            columnId: 'Radiologist',
            headerText: getTitleForWorklistProperty('Radiologist'),
            selectData: (order) => {
                if (!order.OrderProceduresInfos) {
                    return [];
                }
                return order.OrderProceduresInfos.map((op) => {
                    return op.RadiologistInfo ? `${op.RadiologistInfo.Name} ${op.RadiologistInfo.FirstName || ''}` : '';
                });
            },
        }),
        worklistColumnHelper.createStringArrayColumn({
            columnId: 'Technician',
            headerText: getTitleForWorklistProperty('Technician'),
            selectData: (order) => {
                if (!order.OrderProceduresInfos) {
                    return [];
                }
                return order.OrderProceduresInfos.map((op) => op.TechnicianInfo?.Name ?? '');
            },
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'RequestingPhysician',
            headerText: getTitleForWorklistProperty('RequestingPhysician'),
            selectText: (order) => order.OrderInfo.RequestingPhysician ?? '',
        }),
        worklistColumnHelper.createWorklistOrderUrgencyColumn({
            columnId: 'Urgencies',
            headerText: getTitleForWorklistProperty('Urgencies'),
            selectData: (urgency) => urgency,
            renderData: (urgency) => {
                if (urgency.Abbreviation == null) {
                    return '';
                }

                return <Urgency Name={urgency.Name} Abbreviation={urgency.Abbreviation} Color={urgency.Color} />;
            },
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'OrderStatus',
            headerText: getTitleForWorklistProperty('OrderStatus'),
            selectText: (order) => {
                return order.OrderInfo.OrderStatus ? getStatusAsString(order.OrderInfo.OrderStatus) : '';
            },
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'OrderDateTimePrescription',
            headerText: getTitleForWorklistProperty('OrderDateTimePrescription'),
            selectText: (order) => order.OrderInfo.OrderDateTimePrescription ?? '',
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'Remarks',
            headerText: getTitleForWorklistProperty('Remarks'),
            selectData: (order: OrderForWorklist): OrderForWorklist => order,
            renderData: (order: OrderForWorklist) => {
                if (!order.HasRemarks) {
                    return '';
                }
                return (
                    <button
                        className={Styles.remarksButton}
                        onMouseEnter={() => dataGridStore && handleMouseEnter(dataGridStore, order)}
                        onMouseLeave={() => dataGridStore && handleMouseLeave(dataGridStore)}
                        aria-label="View remarks"
                    >
                        <DobIcon style={{ color: 'yellow' }} size="20px" name="exclamation-triangle-fill" />
                    </button>
                );
            },
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'InvoiceStatus',
            headerText: getTitleForWorklistProperty('InvoiceStatus'),
            selectData: (order) => order.OrderInfo.InvoiceStatus,
            renderData: (invoiceStatus: InvoiceStatus | null) => {
                return <InvoiceStatusComponent invoiceStatus={invoiceStatus ?? undefined} />;
            },
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'InvoiceAmount',
            headerText: getTitleForWorklistProperty('InvoiceAmount'),
            selectText: (order) => `€ ${order.OrderInfo.InvoiceAmount?.toFixed(2) ?? '0.00'}`,
        }),
        worklistColumnHelper.createDataColumnDefinition({
            headerContent: undefined,
            columnId: 'ReadyForInvoicing',
            headerText: getTitleForWorklistProperty('ReadyForInvoicing'),
            selectData: (order) => order.OrderInfo.ReadyForInvoicing,
            renderData: (ready: boolean) => <> {ready ? '✅' : '❌'} </>,
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'AdmissionDepartment',
            headerText: getTitleForWorklistProperty('AdmissionDepartment'),
            selectText: (order) => order.PatientInfo.AdmissionDepartment ?? '',
        }),
        worklistColumnHelper.createWorklistTextColumn({
            columnId: 'PatientLocation',
            headerText: getTitleForWorklistProperty('PatientLocation'),
            selectText: (order) => order.PatientInfo.PatientLocation ?? '',
        }),
        worklistColumnHelper.createDataColumnDefinition({
            headerContent: undefined,
            columnId: 'Justification',
            headerText: getTitleForWorklistProperty('Justification'),
            selectData: (order) => ({
                order,
                justificationStatus: order.OrderInfo.JustificationStatus,
            }),
            renderData: ({ order, justificationStatus }) => (
                <JustificationCell
                    justificationStatus={justificationStatus}
                    onClick={() => dataGridStore?.setJustificationOrder(order)}
                />
            ),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportSupervisor',
            headerText: getTitleForWorklistProperty('ReportSupervisor'),
            selectData: (order) => buildAlignedReportValuesWithKeys(order, (r): ReportTextRowValue => ({ text: r.ReportSupervisor ?? '', isOrderTypeReport: r.IsOrderTypeReport }), { text: '', isOrderTypeReport: false }, 'report-supervisor'),
            renderData: (values: readonly AlignedReportValue<ReportTextRowValue>[]) => renderAlignedReportText(values),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportReviewer',
            headerText: getTitleForWorklistProperty('ReportReviewer'),
            selectData: (order) => buildAlignedReportValuesWithKeys(order, (r): ReportTextRowValue => ({ text: r.ReportReviewer ?? '', isOrderTypeReport: r.IsOrderTypeReport }), { text: '', isOrderTypeReport: false }, 'report-reviewer'),
            renderData: (values: readonly AlignedReportValue<ReportTextRowValue>[]) => renderAlignedReportText(values),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportValidatedBy',
            headerText: getTitleForWorklistProperty('ReportValidatedBy'),
            selectData: (order) => buildAlignedReportValuesWithKeys(order, (r): ReportTextRowValue => ({ text: r.ReportValidatedBy ?? '', isOrderTypeReport: r.IsOrderTypeReport }), { text: '', isOrderTypeReport: false }, 'report-validated-by'),
            renderData: (values: readonly AlignedReportValue<ReportTextRowValue>[]) => renderAlignedReportText(values),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportTranscribedBy',
            headerText: getTitleForWorklistProperty('ReportTranscribedBy'),
            selectData: (order) => buildAlignedReportValuesWithKeys(order, (r): ReportTextRowValue => ({ text: r.ReportTranscribedBy ?? '', isOrderTypeReport: r.IsOrderTypeReport }), { text: '', isOrderTypeReport: false }, 'report-transcribed-by'),
            renderData: (values: readonly AlignedReportValue<ReportTextRowValue>[]) => renderAlignedReportText(values),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportCreatedBy',
            headerText: getTitleForWorklistProperty('ReportCreatedBy'),
            selectData: (order) => buildAlignedReportValuesWithKeys(order, (r): ReportTextRowValue => ({ text: r.ReportCreatedBy ?? '', isOrderTypeReport: r.IsOrderTypeReport }), { text: '', isOrderTypeReport: false }, 'report-created-by'),
            renderData: (values: readonly AlignedReportValue<ReportTextRowValue>[]) => renderAlignedReportText(values),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportCreatedDateTime',
            headerText: getTitleForWorklistProperty('ReportCreatedDateTime'),
            selectData: (order) => buildAlignedReportValuesWithKeys(order, (r): ReportTextRowValue => ({ text: formatReportDateTime(r.ReportCreatedDateTime), isOrderTypeReport: r.IsOrderTypeReport }), { text: '', isOrderTypeReport: false }, 'report-created-datetime'),
            renderData: (values: readonly AlignedReportValue<ReportTextRowValue>[]) => renderAlignedReportText(values),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportValidatedDateTime',
            headerText: getTitleForWorklistProperty('ReportValidatedDateTime'),
            selectData: (order) => buildAlignedReportValuesWithKeys(order, (r): ReportTextRowValue => ({ text: formatReportDateTime(r.ReportValidatedDateTime), isOrderTypeReport: r.IsOrderTypeReport }), { text: '', isOrderTypeReport: false }, 'report-validated-datetime'),
            renderData: (values: readonly AlignedReportValue<ReportTextRowValue>[]) => renderAlignedReportText(values),
        }),
        worklistColumnHelper.createWorklistCustomColumn({
            columnId: 'ReportStatus',
            headerText: getTitleForWorklistProperty('ReportStatus'),
            selectData: (order) => ({
                order,
                statusRows: buildAlignedReportValuesWithKeys(
                    order,
                    (r): ReportStatusRowValue => ({
                        status: r.Status,
                        isOrderTypeReport: r.IsOrderTypeReport,
                        isLocked: r.IsLocked,
                        lockedBy: r.LockedBy,
                    }),
                    (procedure): ReportStatusRowValue => ({
                        status: procedure.DictationLockType == null ? null : REPORT_STATUS.Dictating.id,
                        isOrderTypeReport: procedure.DictationLockType === DictationLockType.Order,
                        isLocked: procedure.DictationLockType != null,
                        lockedBy: procedure.DictationLockedBy ?? null,
                    }),
                    'report-status',
                ),
                alignedReports: buildAlignedReports(order),
            }),
            renderData: (data) => <ReportStatusColumnCell {...data} dataGridStore={dataGridStore} />,
        }),
    ];
};
